import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, doc, getDoc, getDocs, orderBy, query, Timestamp } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { functions } from '../../../config/firebase';

type InboxStatus = 'new' | 'processed' | 'needs_review' | 'ignored' | 'matched' | 'applied';

interface ProcessedAllocation {
  registrationId: string;
  amount: number;
}

interface AllocationLine {
  registrationId: string;
  amount: number;
}

interface EtransferInboxItem {
  id: string;
  senderName: string | null;
  senderEmail: string | null;
  extractedAmount: number | null;
  extractedInvoiceNumber: string | null;
  memoText: string | null;
  rawSubject: string | null;
  status: InboxStatus | string;
  receivedAt: Date | null;
  importedAt: Date | null;
  processedAt: Date | null;
  processedRegistrationId: string | null;
  processedAllocations: ProcessedAllocation[];
}

interface RegistrationSummary {
  id: string;
  primaryEmail: string | null;
  primaryName: string | null;
  normalizedPrimaryName: string | null;
  invoiceNumber: string | null;
  openBalance: number;
}

interface SuggestedMatch {
  registrationId: string;
  displayName: string;
  reasonLabel: string;
  reasonCode: string;
  confidence: 'High' | 'Medium';
  autoApplicable: boolean;
}

const MONEY_EPSILON = 0.009;

const roundMoney = (value: number): number => Number(value.toFixed(2));

const sumAllocationAmounts = (allocations: AllocationLine[]): number =>
  roundMoney(allocations.reduce((sum, line) => sum + line.amount, 0));

const canApplyAllocations = (
  allocations: AllocationLine[],
  transferAmount: number
): boolean => {
  if (allocations.length === 0 || transferAmount <= 0) return false;

  const allocatedTotal = sumAllocationAmounts(allocations);
  if (allocatedTotal <= MONEY_EPSILON) return false;
  if (allocatedTotal > transferAmount + MONEY_EPSILON) return false;

  return allocations.every((line) => line.amount > MONEY_EPSILON);
};

const calculateNights = (arrivalDate: any, departureDate: any): number => {
  if (!arrivalDate || !departureDate) return 0;
  const arrival = new Date(arrivalDate);
  const departure = new Date(departureDate);
  const timeDiff = departure.getTime() - arrival.getTime();
  const nights = Math.ceil(timeDiff / (1000 * 3600 * 24));
  return nights > 0 ? nights : 0;
};

const normalizeName = (value: string | null): string | null => {
  if (!value) return null;
  const normalized = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .trim();
  return normalized || null;
};

const EtransferInboxReport: React.FC = () => {
  const [records, setRecords] = useState<EtransferInboxItem[]>([]);
  const [eligibleRegistrations, setEligibleRegistrations] = useState<RegistrationSummary[]>([]);
  const [registrationsById, setRegistrationsById] = useState<Map<string, RegistrationSummary>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'new' | 'processed' | 'needs_review' | 'ignored'>('all');
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [manualSearchTerms, setManualSearchTerms] = useState<Record<string, string>>({});
  const [manualPendingSelections, setManualPendingSelections] = useState<Record<string, string>>({});
  const [manualAllocations, setManualAllocations] = useState<Record<string, AllocationLine[]>>({});

  const normalizeEmail = (value: string | null): string | null => value ? value.trim().toLowerCase() : null;
  const normalizeInvoice = (value: string | null): string | null => value ? value.trim().toUpperCase() : null;

  const getStatusLabel = (status: string): 'new' | 'processed' | 'needs_review' | 'ignored' => {
    const normalized = status.toLowerCase();
    if (normalized === 'processed' || normalized === 'applied') return 'processed';
    if (normalized === 'needs_review') return 'needs_review';
    if (normalized === 'ignored') return 'ignored';
    return 'new';
  };

  // Mirrors the live balance calculation used by Registration Details / Invoice so the
  // "owed" amount here matches what admins see on the registration record.
  const calculateOpenBalance = (registration: any, settings: any): number => {
    let grandTotal = 0;

    // Accommodation fees
    if (registration?.financial?.accommodationFees?.assignedAccommodation && settings?.rates?.accommodation) {
      grandTotal += Number(registration.financial.accommodationFees.accommodationFee) || 0;
    }

    // Registration fees (recomputed from live rates and current campers/nights)
    if (Array.isArray(registration?.attendingCampers) && settings?.rates?.registration) {
      registration.attendingCampers.forEach((camper: any) => {
        const nights = camper.nights || calculateNights(registration?.campDates?.arrivalDate, registration?.campDates?.departureDate);
        const isFullCamp = nights >= 5;

        let rate = 0;
        if (camper.age === 'Adult' && settings.rates.registration.adult) {
          rate = isFullCamp ? (settings.rates.registration.adult.weekly || 0) : (settings.rates.registration.adult.nightly || 0) * nights;
        } else if (camper.age === 'AdultStudent' && settings.rates.registration.adultStudent) {
          rate = isFullCamp ? (settings.rates.registration.adultStudent.weekly || 0) : (settings.rates.registration.adultStudent.nightly || 0) * nights;
        } else if (camper.age === 'YouthChildren' && settings.rates.registration.youthAndChildren) {
          rate = isFullCamp ? (settings.rates.registration.youthAndChildren.weekly || 0) : (settings.rates.registration.youthAndChildren.nightly || 0) * nights;
        }
        grandTotal += rate;
      });
    }

    // Meal fees (recomputed from live rates) - check new path then legacy
    if (settings?.rates?.meals) {
      const fullCampAdultRate = settings.rates.meals.fullCampAdult || 0;
      const fullCampChildRate = settings.rates.meals.fullCampChild || 0;
      const dailyAdultRate = settings.rates.meals.dailyAdult || 0;
      const dailyChildRate = settings.rates.meals.dailyChild || 0;

      const meals = registration?.financial?.otherFees?.meals;
      if (meals) {
        grandTotal += (meals.fullCampPassesAdult?.quantity || 0) * fullCampAdultRate;
        grandTotal += (meals.fullCampPassesChild?.quantity || 0) * fullCampChildRate;
        grandTotal += (meals.dailyPassesAdult?.quantity || 0) * dailyAdultRate;
        grandTotal += (meals.dailyPassesChild?.quantity || 0) * dailyChildRate;
      } else {
        const legacyMeals = registration?.financial?.mealFees || registration?.mealFees;
        if (legacyMeals) {
          const fullCampAdult = legacyMeals.fullCampPassesAdult || legacyMeals.fullCampPasses?.adult || 0;
          const fullCampChild = legacyMeals.fullCampPassesChild || legacyMeals.fullCampPasses?.child || 0;
          const dailyAdult = legacyMeals.dailyPassesAdult || legacyMeals.dailyPasses?.adult || 0;
          const dailyChild = legacyMeals.dailyPassesChild || legacyMeals.dailyPasses?.child || 0;
          grandTotal += fullCampAdult * fullCampAdultRate;
          grandTotal += fullCampChild * fullCampChildRate;
          grandTotal += dailyAdult * dailyAdultRate;
          grandTotal += dailyChild * dailyChildRate;
        }
      }
    }

    // Other fees (fridge, donations, custom, array items)
    if (registration?.financial?.otherFees) {
      const otherFees = registration.financial.otherFees;
      grandTotal += Number(otherFees.fridge?.cost ?? otherFees.fridgeRental ?? 0) || 0;
      grandTotal += Number(otherFees.donations?.amount ?? otherFees.donation ?? 0) || 0;
      grandTotal += Number(otherFees.customFee?.amount) || 0;
      for (const fee of (otherFees.otherFees || [])) {
        grandTotal += Number(fee?.amount) || 0;
      }
    }

    // Enabled overrides
    const savedOverrides = registration?.financial?.overrides;
    if (savedOverrides?.accommodation?.enabled) {
      grandTotal += Number(savedOverrides.accommodation.amount) || 0;
    }
    if (savedOverrides?.registration?.enabled) {
      grandTotal += Number(savedOverrides.registration.amount) || 0;
    }
    if (savedOverrides?.mealFees?.enabled) {
      grandTotal += Number(savedOverrides.mealFees.amount) || 0;
    }
    if (savedOverrides?.otherFees?.enabled) {
      grandTotal += Number(savedOverrides.otherFees.amount) || 0;
    }

    const paymentHistory = Array.isArray(registration?.paymentHistory)
      ? registration.paymentHistory
      : (Array.isArray(registration?.payments) ? registration.payments : []);
    const totalPayments = paymentHistory.reduce((sum: number, payment: any) => {
      const amount = Number(payment?.amount || 0);
      return sum + (Number.isFinite(amount) ? amount : 0);
    }, 0);

    return Number((grandTotal - totalPayments).toFixed(2));
  };

  const fetchInbox = async () => {
    try {
      setLoading(true);
      setError(null);
      setActionError(null);

      const inboxQuery = query(collection(db, 'etransferInbox'), orderBy('importedAt', 'desc'));
      const [inboxSnapshot, registrationSnapshot, settingsSnap] = await Promise.all([
        getDocs(inboxQuery),
        getDocs(collection(db, 'Registrations')),
        getDoc(doc(db, 'settings', 'camp')),
      ]);

      const settingsData = settingsSnap.exists() ? settingsSnap.data() : null;

      const items: EtransferInboxItem[] = inboxSnapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        const toDateOrNull = (value: any): Date | null => {
          if (value instanceof Timestamp) return value.toDate();
          if (value instanceof Date) return value;
          if (value?.toDate instanceof Function) return value.toDate();
          return null;
        };

        const processedAllocations: ProcessedAllocation[] = Array.isArray(data.processedAllocations)
          ? data.processedAllocations
            .map((entry: any) => {
              const registrationId = typeof entry?.registrationId === 'string' ? entry.registrationId : '';
              const amount = roundMoney(Number(entry?.amount));
              if (!registrationId || !Number.isFinite(amount) || amount <= 0) return null;
              return { registrationId, amount };
            })
            .filter((entry: ProcessedAllocation | null): entry is ProcessedAllocation => entry !== null)
          : [];

        return {
          id: docSnap.id,
          senderName: data.senderName || null,
          senderEmail: data.senderEmail || null,
          extractedAmount: typeof data.extractedAmount === 'number' ? data.extractedAmount : null,
          extractedInvoiceNumber: data.extractedInvoiceNumber || null,
          memoText: data.memoText || null,
          rawSubject: data.rawSubject || null,
          status: data.status || 'new',
          receivedAt: toDateOrNull(data.receivedAt),
          importedAt: toDateOrNull(data.importedAt),
          processedAt: toDateOrNull(data.processedAt),
          processedRegistrationId: data.processedRegistrationId || null,
          processedAllocations,
        };
      });

      const registrationItems: RegistrationSummary[] = registrationSnapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        const primaryName = (
          data?.registrationInfo?.primaryCamperName ||
          `${data?.firstName || ''} ${data?.lastName || ''}`
        ).trim();

        return {
          id: docSnap.id,
          primaryEmail: normalizeEmail(data?.registrationInfo?.email || data?.email || null),
          primaryName: primaryName || null,
          normalizedPrimaryName: normalizeName(primaryName || null),
          invoiceNumber: normalizeInvoice(data?.invoiceNumber || data?.financial?.invoiceNumber || null),
          openBalance: calculateOpenBalance(data, settingsData),
        };
      });

      setRecords(items);
      setEligibleRegistrations(registrationItems.filter((item) => item.openBalance > 0.009));
      setRegistrationsById(new Map(registrationItems.map((item) => [item.id, item])));
    } catch (err) {
      console.error('Error loading e-transfer inbox:', err);
      setError('Failed to load e-transfer inbox records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInbox();
  }, []);

  const filteredRecords = useMemo(() => {
    const normalizedTerm = searchTerm.trim().toLowerCase();
    return records.filter((record) => {
      const normalizedStatus = getStatusLabel(record.status || 'new');
      if (statusFilter !== 'all' && normalizedStatus !== statusFilter) return false;
      if (!normalizedTerm) return true;

      const haystack = [
        record.senderName || '',
        record.senderEmail || '',
        record.extractedInvoiceNumber || '',
        record.memoText || '',
        record.rawSubject || '',
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalizedTerm);
    });
  }, [records, searchTerm, statusFilter]);

  const formatCurrency = (amount: number | null): string => {
    if (amount === null) return '-';
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  const formatDateTime = (value: Date | null): string => {
    if (!value) return '-';
    return value.toLocaleString('en-CA');
  };

  const formatRegistrationDisplay = (registration: RegistrationSummary): string => {
    const name = registration.primaryName || 'Unknown';
    return `${name} — ${formatCurrency(registration.openBalance)} owed`;
  };

  const formatAllocationLineLabel = (registrationId: string, amount: number): string => {
    const registration = registrationsById.get(registrationId);
    const name = registration?.primaryName || 'Unknown';
    return `${name} — ${formatCurrency(amount)}`;
  };

  const formatRegistrationIdDisplay = (registrationId: string | null): string => {
    if (!registrationId) return '-';
    const registration = registrationsById.get(registrationId);
    return registration ? formatRegistrationDisplay(registration) : '-';
  };

  const formatProcessedAllocationsDisplay = (record: EtransferInboxItem): string => {
    if (record.processedAllocations.length > 0) {
      return record.processedAllocations
        .map((allocation) => formatAllocationLineLabel(allocation.registrationId, allocation.amount))
        .join('; ');
    }
    return formatRegistrationIdDisplay(record.processedRegistrationId);
  };

  const buildSuggestion = (record: EtransferInboxItem): SuggestedMatch | null => {
    const amount = record.extractedAmount;
    if (amount === null || amount <= 0) return null;

    const senderName = normalizeName(record.senderName);
    if (senderName) {
      const nameMatches = eligibleRegistrations.filter((registration) => registration.normalizedPrimaryName === senderName);
      if (nameMatches.length === 1) {
        const match = nameMatches[0];
        return {
          registrationId: match.id,
          displayName: formatRegistrationDisplay(match),
          reasonLabel: 'Primary camper name match',
          reasonCode: 'primary_name_exact',
          confidence: 'Medium',
          autoApplicable: true,
        };
      }
    }

    return null;
  };

  const getDefaultAllocationAmount = (
    record: EtransferInboxItem,
    registrationId: string,
    existingAllocations: AllocationLine[]
  ): number | null => {
    const transferAmount = record.extractedAmount;
    if (transferAmount === null || transferAmount <= 0) return null;

    const registration = registrationsById.get(registrationId);
    if (!registration) return null;

    const remaining = roundMoney(transferAmount - sumAllocationAmounts(existingAllocations));
    if (remaining <= MONEY_EPSILON) return null;

    return remaining;
  };

  const addAllocation = (
    record: EtransferInboxItem,
    registrationId: string,
    amountOverride?: number
  ): void => {
    const existingAllocations = manualAllocations[record.id] || [];
    if (existingAllocations.some((line) => line.registrationId === registrationId)) {
      setActionError('That registration is already in the allocation list.');
      return;
    }

    const amount = amountOverride ?? getDefaultAllocationAmount(record, registrationId, existingAllocations);
    if (amount === null || amount <= 0) {
      setActionError('No remaining e-transfer amount to allocate.');
      return;
    }

    setActionError(null);
    setManualAllocations((prev) => ({
      ...prev,
      [record.id]: [...existingAllocations, { registrationId, amount }],
    }));
    setManualPendingSelections((prev) => {
      const next = { ...prev };
      delete next[record.id];
      return next;
    });
  };

  const updateAllocationAmount = (recordId: string, registrationId: string, rawValue: string): void => {
    const parsed = roundMoney(Number(rawValue));
    setManualAllocations((prev) => {
      const lines = prev[recordId] || [];
      return {
        ...prev,
        [recordId]: lines.map((line) =>
          line.registrationId === registrationId
            ? { ...line, amount: Number.isFinite(parsed) && parsed > 0 ? parsed : line.amount }
            : line
        ),
      };
    });
  };

  const removeAllocation = (recordId: string, registrationId: string): void => {
    setManualAllocations((prev) => {
      const lines = prev[recordId] || [];
      const nextLines = lines.filter((line) => line.registrationId !== registrationId);
      if (nextLines.length === 0) {
        const next = { ...prev };
        delete next[recordId];
        return next;
      }
      return { ...prev, [recordId]: nextLines };
    });
  };

  const clearRecordDraftState = (recordId: string): void => {
    setManualSearchTerms((prev) => {
      const next = { ...prev };
      delete next[recordId];
      return next;
    });
    setManualPendingSelections((prev) => {
      const next = { ...prev };
      delete next[recordId];
      return next;
    });
    setManualAllocations((prev) => {
      const next = { ...prev };
      delete next[recordId];
      return next;
    });
  };

  const handleApply = async (
    record: EtransferInboxItem,
    options?: { singleRegistrationId?: string; allocations?: AllocationLine[] }
  ): Promise<void> => {
    const suggestion = buildSuggestion(record);
    const allocations = options?.allocations || [];
    const registrationId = options?.singleRegistrationId || suggestion?.registrationId;
    const matchReason = allocations.length > 1
      ? 'manual_split'
      : allocations.length === 1
        ? 'manual_split'
        : (options?.singleRegistrationId ? 'manual_name_search' : suggestion?.reasonCode);

    try {
      setActionError(null);
      setApplyingId(record.id);
      const applyPayment = httpsCallable(functions, 'applyEtransferInboxPayment');
      const payload: {
        inboxItemId: string;
        matchReason: string;
        registrationId?: string;
        allocations?: { registrationId: string; amount: number }[];
      } = {
        inboxItemId: record.id,
        matchReason: matchReason || 'manual_selection',
      };

      if (allocations.length > 0) {
        payload.allocations = allocations.map((line) => ({
          registrationId: line.registrationId,
          amount: roundMoney(line.amount),
        }));
        // Fallback for backends that only accept a single registrationId.
        payload.registrationId = allocations[0].registrationId;
      } else if (registrationId) {
        payload.registrationId = registrationId;
      }

      await applyPayment(payload);
      clearRecordDraftState(record.id);
      await fetchInbox();
    } catch (err: any) {
      console.error('Failed to apply e-transfer payment:', err);
      setActionError(err?.message || 'Failed to apply payment.');
    } finally {
      setApplyingId(null);
    }
  };

  const renderAllocationTally = (record: EtransferInboxItem, allocations: AllocationLine[]) => {
    const transferAmount = record.extractedAmount;
    if (transferAmount === null) return null;

    const allocatedTotal = sumAllocationAmounts(allocations);
    const remaining = roundMoney(transferAmount - allocatedTotal);
    const isBalanced = Math.abs(remaining) <= MONEY_EPSILON;

    return (
      <div
        style={{
          padding: '8px',
          borderRadius: '4px',
          background: isBalanced ? '#e8f5e9' : '#fff8e1',
          fontSize: '12px',
          lineHeight: 1.5,
        }}
      >
        <div><strong>E-transfer:</strong> {formatCurrency(transferAmount)}</div>
        <div><strong>Allocated:</strong> {formatCurrency(allocatedTotal)}</div>
        <div>
          <strong>Remaining:</strong>{' '}
          <span style={{ color: isBalanced ? '#1b5e20' : '#9a6700' }}>
            {formatCurrency(remaining)}
          </span>
        </div>
      </div>
    );
  };

  const renderAllocationBuilder = (record: EtransferInboxItem, suggestion: SuggestedMatch | null) => {
    const allocations = manualAllocations[record.id] || [];
    const manualSearchTerm = manualSearchTerms[record.id] || '';
    const normalizedManualTerm = manualSearchTerm.trim().toLowerCase();
    const normalizedManualNameTerm = normalizeName(manualSearchTerm);
    const pendingRegistrationId = manualPendingSelections[record.id] || '';
    const transferAmount = record.extractedAmount ?? 0;
    const allocatedTotal = sumAllocationAmounts(allocations);

    const manualCandidates = normalizedManualTerm
      ? eligibleRegistrations
        .filter((registration) => {
          if (allocations.some((line) => line.registrationId === registration.id)) return false;
          const rawName = (registration.primaryName || '').toLowerCase();
          const normalizedName = registration.normalizedPrimaryName || '';
          return (
            rawName.includes(normalizedManualTerm) ||
            (!!normalizedManualNameTerm && normalizedName.includes(normalizedManualNameTerm))
          );
        })
        .slice(0, 10)
      : [];

    const canAddPending = !!pendingRegistrationId &&
      getDefaultAllocationAmount(record, pendingRegistrationId, allocations) !== null;

    const canAddSuggestion = !!suggestion &&
      !allocations.some((line) => line.registrationId === suggestion.registrationId) &&
      getDefaultAllocationAmount(record, suggestion.registrationId, allocations) !== null;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '260px' }}>
        {suggestion ? (
          <span style={{ color: '#1b5e20', fontWeight: 500 }}>{suggestion.confidence} confidence</span>
        ) : (
          <span style={{ color: '#9a6700', fontWeight: 500 }}>No auto-match</span>
        )}

        {allocations.length > 0 && renderAllocationTally(record, allocations)}

        {allocations.length > 0 && (
          <ul style={{ margin: 0, paddingLeft: '18px' }}>
            {allocations.map((line) => (
              <li key={line.registrationId} style={{ marginBottom: '6px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '12px' }}>
                    {formatRegistrationIdDisplay(line.registrationId)}
                  </span>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <label style={{ fontSize: '12px' }}>
                      Apply:
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={line.amount}
                        onChange={(e) => updateAllocationAmount(record.id, line.registrationId, e.target.value)}
                        style={{ width: '90px', marginLeft: '4px' }}
                      />
                    </label>
                    <button
                      type="button"
                      className="back-to-reports"
                      onClick={() => removeAllocation(record.id, line.registrationId)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {canAddSuggestion && (
          <button
            type="button"
            className="back-to-reports"
            onClick={() => addAllocation(record, suggestion.registrationId)}
          >
            Add suggested match
          </button>
        )}

        <input
          type="text"
          value={manualSearchTerm}
          onChange={(e) => {
            setManualSearchTerms((prev) => ({ ...prev, [record.id]: e.target.value }));
            setManualPendingSelections((prev) => {
              if (!prev[record.id]) return prev;
              const next = { ...prev };
              delete next[record.id];
              return next;
            });
          }}
          placeholder="Search registration name..."
        />

        {manualCandidates.length > 0 && (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              style={{ minWidth: '220px', maxWidth: '100%', flex: '1 1 auto' }}
              value={pendingRegistrationId}
              onChange={(e) => {
                setManualPendingSelections((prev) => ({
                  ...prev,
                  [record.id]: e.target.value,
                }));
              }}
            >
              <option value="">Select registration...</option>
              {manualCandidates.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {formatRegistrationDisplay(candidate)}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="back-to-reports"
              disabled={!canAddPending}
              onClick={() => addAllocation(record, pendingRegistrationId)}
            >
              Add
            </button>
          </div>
        )}

        {normalizedManualTerm && manualCandidates.length === 0 && (
          <span style={{ color: '#666', fontSize: '12px' }}>
            {allocations.length > 0
              ? 'No additional registrations match that search.'
              : 'No name matches found.'}
          </span>
        )}

        {transferAmount > 0 && allocatedTotal > transferAmount + MONEY_EPSILON && (
          <span style={{ color: '#9a6700', fontSize: '12px' }}>
            Allocated total cannot exceed the e-transfer amount.
          </span>
        )}
      </div>
    );
  };

  if (loading) return <div>Loading e-transfer inbox...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="report-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h2>E-Transfer Inbox</h2>
        <button className="back-to-reports" onClick={fetchInbox}>
          Refresh
        </button>
      </div>

      <p style={{ marginTop: 0, color: '#555' }}>
        Assign an e-transfer to one or more registrations using Add, then apply. Overpayments create a credit on the registration.
      </p>

      {actionError && <div className="error">{actionError}</div>}

      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div>
          <label htmlFor="etransfer-status-filter">Status: </label>
          <select
            id="etransfer-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'new' | 'processed' | 'needs_review' | 'ignored')}
          >
            <option value="all">All</option>
            <option value="new">New</option>
            <option value="processed">Processed</option>
            <option value="needs_review">Needs Review</option>
            <option value="ignored">Ignored</option>
          </select>
        </div>

        <div>
          <label htmlFor="etransfer-search">Search: </label>
          <input
            id="etransfer-search"
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Sender, invoice #, memo..."
          />
        </div>
      </div>

      <div className="summary-section" style={{ marginBottom: '16px' }}>
        <div className="summary-card">
          <h3>Total Imported</h3>
          <p>{records.length}</p>
        </div>
        <div className="summary-card">
          <h3>Currently Visible</h3>
          <p>{filteredRecords.length}</p>
        </div>
      </div>

      <div className="table-container">
        <table className="payment-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Suggested Match</th>
              <th>Allocation</th>
              <th>Action</th>
              <th>Amount</th>
              <th>Invoice #</th>
              <th>Sender</th>
              <th>Memo</th>
              <th>Subject</th>
              <th>Received</th>
              <th>Imported</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.map((record) => {
              const normalizedStatus = getStatusLabel(record.status || 'new');
              const suggestion = buildSuggestion(record);
              const allocations = manualAllocations[record.id] || [];
              const transferAmount = record.extractedAmount ?? 0;
              const canApplyAllocationsForRecord = canApplyAllocations(allocations, transferAmount);
              const canApplySingle = allocations.length === 0 && !!suggestion?.registrationId;

              return (
              <tr key={record.id}>
                <td>{normalizedStatus}</td>
                <td>
                  {normalizedStatus === 'processed'
                    ? formatProcessedAllocationsDisplay(record)
                    : allocations.length > 0
                      ? allocations.map((line) => formatAllocationLineLabel(line.registrationId, line.amount)).join('; ')
                      : suggestion?.displayName || '-'}
                </td>
                <td>
                  {normalizedStatus === 'processed'
                    ? 'Applied'
                    : renderAllocationBuilder(record, suggestion)}
                </td>
                <td>
                  {normalizedStatus === 'processed' ? (
                    <>Processed</>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {canApplyAllocationsForRecord && (
                        <button
                          className="back-to-reports"
                          onClick={() => handleApply(record, {
                            allocations: manualAllocations[record.id] || [],
                          })}
                          disabled={applyingId === record.id}
                          title="Apply allocation to selected registrations"
                        >
                          {applyingId === record.id
                            ? 'Applying...'
                            : (allocations.length > 1 ? 'Apply split' : 'Apply payment')}
                        </button>
                      )}
                      {canApplySingle && (
                        <button
                          className="back-to-reports"
                          onClick={() => handleApply(record, { singleRegistrationId: suggestion.registrationId })}
                          disabled={applyingId === record.id}
                          title={suggestion.reasonLabel}
                        >
                          {applyingId === record.id ? 'Applying...' : 'Apply full amount'}
                        </button>
                      )}
                      {!canApplyAllocationsForRecord && !canApplySingle && allocations.length === 0 && (
                        <button
                          className="back-to-reports"
                          onClick={() => handleApply(record)}
                          disabled={applyingId === record.id}
                          title="Attempts name auto-match on backend"
                        >
                          {applyingId === record.id ? 'Applying...' : 'Try Match'}
                        </button>
                      )}
                    </div>
                  )}
                </td>
                <td>{formatCurrency(record.extractedAmount)}</td>
                <td>{record.extractedInvoiceNumber || '-'}</td>
                <td>{record.senderName || record.senderEmail || '-'}</td>
                <td>{record.memoText || '-'}</td>
                <td>{record.rawSubject || '-'}</td>
                <td>{formatDateTime(record.receivedAt)}</td>
                <td>{formatDateTime(record.importedAt)}</td>
              </tr>
            )})}
          </tbody>
        </table>
      </div>

      {filteredRecords.length === 0 && (
        <div className="no-records">No e-transfer inbox records match the current filters.</div>
      )}
    </div>
  );
};

export default EtransferInboxReport;
